<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import LiveMap from '@/Components/GPS/LiveMap.vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  vehicles:  { type: Array, default: () => [] },
  positions: { type: Array, default: () => [] },
  error:     { type: String, default: null },
})

const livePositions = ref([...props.positions])
let pollInterval    = null

async function fetchPositions() {
  try {
    const res  = await fetch('/gps/positions', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const data = await res.json()
    livePositions.value = data.positions || []
  } catch {
    // silently keep last known positions on network error
  }
}

onMounted(() => {
  pollInterval = setInterval(fetchPositions, 15_000)
})

onBeforeUnmount(() => {
  clearInterval(pollInterval)
})
</script>

<template>
  <PageShell title="Fleet Map" :subtitle="'Updates every 15 seconds'">
    <div
      v-if="error"
      class="rounded-[var(--radius-md)] px-4 py-3 text-sm mb-4"
      :style="{ background: 'var(--dangerSoft)', border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)', color: 'var(--danger)' }"
    >
      {{ error }}
    </div>

    <div
      v-if="vehicles.length === 0 && !error"
      class="rounded-[var(--radius-md)] px-4 py-3 text-sm mb-4"
      :style="{ background: 'var(--warnSoft)', border: '1px solid color-mix(in oklab, var(--warn) 35%, transparent)', color: 'var(--warn)' }"
    >
      No vehicles have GPS trackers linked yet. Go to a vehicle page → GPS tab to link a tracker.
    </div>

    <div class="flex flex-col-reverse md:flex-row md:gap-4">
      <!-- Vehicle list — bottom sheet on mobile, side panel on desktop -->
      <aside
        class="md:w-72 md:shrink-0 md:max-h-[calc(100vh-200px)] overflow-y-auto
               md:rounded-[var(--radius-lg)] mt-4 md:mt-0
               bg-[color:var(--surface)] border border-[color:var(--line)]"
      >
        <div class="px-4 py-3 border-b border-[color:var(--line)]">
          <h2 class="text-sm font-semibold text-[color:var(--inkSoft)]">Tracked Vehicles ({{ vehicles.length }})</h2>
        </div>
        <div
          v-if="vehicles.length === 0"
          class="px-4 py-6 text-sm text-[color:var(--muted)] text-center"
        >No tracked vehicles.</div>
        <ul v-else class="divide-y divide-[color:var(--line)]">
          <li
            v-for="v in vehicles"
            :key="v.id"
            class="px-4 py-3 flex items-center justify-between text-sm"
          >
            <div>
              <span class="font-medium text-[color:var(--ink)]">{{ v.make }} {{ v.model }}</span>
              <span class="ml-2 text-[color:var(--muted)] font-mono text-xs">{{ v.registration }}</span>
            </div>
            <Link
              :href="`/vehicles/${v.id}/gps`"
              class="text-[color:var(--accent)] hover:underline text-xs"
            >GPS →</Link>
          </li>
        </ul>
      </aside>

      <!-- Map — full-bleed on mobile, fills remaining space on desktop -->
      <div class="flex-1 -mx-4 md:mx-0">
        <LiveMap
          :positions="livePositions"
          :vehicles="vehicles"
          :zoom="13"
          class="!h-[60vh] md:!h-[calc(100vh-200px)] md:!rounded-[var(--radius-lg)]"
        />
      </div>
    </div>
  </PageShell>
</template>
